import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Typography, Table, Tag, Select, Space, Button, Modal, Input, Form, message, Segmented } from 'antd';
import { PlusOutlined, BulbOutlined, UnorderedListOutlined, UserOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';

const STATUS_COLOR = {
  submitted:      'blue',
  in_review:      'orange',
  in_development: 'processing',
  completed:      'green',
  canceled:       'red',
};

const STATUS_LABEL = {
  submitted:      'Submitted',
  in_review:      'In Review',
  in_development: 'In Development',
  completed:      'Completed',
  canceled:       'Canceled',
};

const STATUS_OPTIONS = [
  { label: 'Submitted',      value: 'submitted' },
  { label: 'In Review',      value: 'in_review' },
  { label: 'In Development', value: 'in_development' },
  { label: 'Completed',      value: 'completed' },
  { label: 'Canceled',       value: 'canceled' },
];

const strSort = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
const numSort = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0);

const FILTER_KEY = 'ideasPageFilters';

function savedFilter(key, fallback) {
  try {
    const v = JSON.parse(sessionStorage.getItem(FILTER_KEY) || '{}')[key];
    return v === undefined ? fallback : v;
  } catch {
    return fallback;
  }
}

export default function IdeasPage() {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);

  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  // Filters survive leaving the page and coming back (Back button) for the
  // rest of the browser tab's session.
  const [statusFilter, setStatusFilter] = useState(() => savedFilter('status', ['submitted', 'in_review', 'in_development']));
  const [teamFilter,   setTeamFilter]   = useState(() => savedFilter('team', null));
  const [teamOptions,  setTeamOptions]  = useState([]);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [userOptions,  setUserOptions]  = useState([]);
  const [stagedFiles,  setStagedFiles]  = useState([]);
  const [viewMode,     setViewMode]     = useState(() => savedFilter('view', 'list'));

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_KEY, JSON.stringify({ status: statusFilter, team: teamFilter, view: viewMode }));
    } catch { /* storage unavailable: filters just reset next time */ }
  }, [statusFilter, teamFilter, viewMode]);
  const [form] = Form.useForm();
  const isAdmin = currentUser?.Role === 'admin';
  const isMyIdeas = viewMode === 'my_ideas';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isMyIdeas) {
        params.my_ideas = '1';
      } else {
        if (statusFilter?.length) params.status  = statusFilter.join(',');
        if (teamFilter)           params.team_id = teamFilter;
      }
      const res = await api.get('/api/ideas', { params });
      setRows(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [statusFilter, teamFilter, isMyIdeas]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) {
      api.get('/api/users').then((r) =>
        setUserOptions((r.data || []).map((u) => ({ label: u.DisplayName || u.Email, value: u.ID })))
      ).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    api.get('/api/teams').then((r) => {
      const all    = r.data || [];
      const vtIds  = (currentUser?.VisibleTeams || []).map((t) => t.ID);
      const visible = vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
      setTeamOptions(visible.map((t) => ({ label: t.Name, value: t.ID })));
    }).catch(() => {});
  }, [currentUser]);

  const handleIdeaPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) setStagedFiles((prev) => [
          ...prev,
          { uid: `${Date.now()}-${Math.random()}`, name: f.name, type: f.type, file: f, previewUrl: URL.createObjectURL(f) },
        ]);
      }
    }
  };

  const removeStagedFile = (uid) => setStagedFiles((prev) => {
    const f = prev.find((x) => x.uid === uid);
    if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
    return prev.filter((x) => x.uid !== uid);
  });

  const clearModal = () => {
    setModalOpen(false);
    form.resetFields();
    setStagedFiles((prev) => { prev.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); }); return []; });
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      const body = {
        short_description: values.short_description,
        idea_details:      values.idea_details,
      };
      if (isAdmin && values.user_id) body.user_id = values.user_id;
      const res = await api.post('/api/ideas', body);
      const { id, comment_id } = res.data;
      if (stagedFiles.length && comment_id) {
        await Promise.all(stagedFiles.map((f) => {
          const fd = new FormData();
          fd.append('comment_type', 'idea');
          fd.append('comment_id', comment_id);
          fd.append('file', f.file);
          return api.post('/api/attachments', fd);
        }));
      }
      message.success('Idea submitted!');
      clearModal();
      navigate(`/ideas/${id}`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to submit idea.');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'IdeaID', key: 'IdeaID', width: 65, sorter: numSort('IdeaID') },
    { title: 'Submitted By', dataIndex: 'UserName',  key: 'UserName',  width: 160, ellipsis: true, sorter: strSort('UserName') },
    { title: 'Team',         dataIndex: 'TeamName',  key: 'TeamName',  width: 140, ellipsis: true, sorter: strSort('TeamName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Short Description', dataIndex: 'ShortDescription', key: 'ShortDescription', ellipsis: true, sorter: strSort('ShortDescription') },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 140,
      render: (v) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v] || v}</Tag> },
    { title: 'Submitted',  dataIndex: 'CreatedAt',   key: 'CreatedAt',   width: 150, sorter: strSort('CreatedAt'),
      render: (v) => formatTS(v) },
    { title: 'Completed',  dataIndex: 'CompletedAt', key: 'CompletedAt', width: 150, sorter: strSort('CompletedAt'),
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Canceled',   dataIndex: 'CanceledAt',  key: 'CanceledAt',  width: 150, sorter: strSort('CanceledAt'),
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <BulbOutlined style={{ marginRight: 8 }} />Ideas
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Submit Idea
        </Button>
      </div>

      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Have an idea on how to improve Harbor?!
      </Typography.Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { label: <span><UnorderedListOutlined style={{ marginRight: 4 }} />List</span>, value: 'list' },
            { label: <span><UserOutlined style={{ marginRight: 4 }} />My Ideas</span>, value: 'my_ideas' },
          ]}
        />
        {!isMyIdeas && (
          <>
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
              style={{ minWidth: 240 }}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Filter by team"
              value={teamFilter}
              onChange={(v) => setTeamFilter(v ?? null)}
              options={teamOptions}
              style={{ minWidth: 180 }}
            />
          </>
        )}
        <Button onClick={load}>Refresh</Button>
      </div>

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="IdeaID"
        loading={loading}
        size="small"
        onRow={(r) => ({ onClick: () => navigate(`/ideas/${r.IdeaID}`), style: { cursor: 'pointer' } })}
        pagination={{ defaultPageSize: 25, showSizeChanger: true }}
      />

      <Modal maskClosable={false}
        title={<Space><BulbOutlined />Submit an Idea</Space>}
        open={modalOpen}
        onCancel={clearModal}
        onOk={handleSubmit}
        okText="Submit"
        confirmLoading={submitting}
        width={600}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Have an idea on how to improve Harbor?!
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          {isAdmin && (
            <Form.Item
              name="user_id"
              label="Submitted By"
              rules={[{ required: true, message: 'Please select the employee' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select employee"
                options={userOptions}
              />
            </Form.Item>
          )}
          <Form.Item
            name="short_description"
            label="Short Description"
            rules={[
              { required: true, message: 'Please enter a short description' },
              { max: 100, message: 'Maximum 100 characters' },
            ]}
          >
            <Input placeholder="Brief summary of your idea" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            name="idea_details"
            label="Idea Details"
            rules={[{ required: true, message: 'Please describe your idea in detail' }]}
          >
            <div onPaste={handleIdeaPaste}>
              <Input.TextArea rows={6} placeholder="Describe your idea… (paste images directly)" />
            </div>
          </Form.Item>
          {stagedFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -8, marginBottom: 12 }}>
              {stagedFiles.map((f) => (
                <div key={f.uid} style={{ position: 'relative', display: 'inline-block' }}>
                  {f.previewUrl
                    ? <img src={f.previewUrl} alt={f.name} style={{ height: 64, maxWidth: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--ant-color-border)' }} />
                    : <div style={{ padding: '8px 12px', border: '1px solid var(--ant-color-border)', borderRadius: 4, fontSize: 12 }}>{f.name}</div>
                  }
                  <CloseCircleFilled
                    onClick={() => removeStagedFile(f.uid)}
                    style={{ position: 'absolute', top: -6, right: -6, color: 'var(--ant-color-error)', cursor: 'pointer', fontSize: 14, background: 'var(--ant-color-bg-container)', borderRadius: '50%' }}
                  />
                </div>
              ))}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
}
